/**
 * Замена блока техкарты — ключевое взаимодействие макета v2-05.
 *
 * Сценарий гл. агронома: щёлкнул плитку блока («Система защиты»), увидел
 * варианты этого блока из ДРУГИХ вариантов той же культуры, выбрал —
 * и ДО сохранения получил ответ «что станет с вариантом»: прямые затраты,
 * себестоимость тонны, затраты по площади, число операций и дельта.
 *
 * Дальше две кнопки, и вторая подсвечена намеренно (рекомендация дизайнера):
 *   • «Заменить блок в этом варианте» — правит текущую карту;
 *   • «Сохранить как новый вариант» — рекомендуемое: сравнивать «Интенсив» и
 *     «Интенсив-эконом» можно только когда оба существуют. Затирание
 *     исходного варианта обратной кнопки не имеет.
 *
 * Инвариант, который держит бэк (ADR-066 D5) и который мы обещаем текстом:
 * трогаются ТОЛЬКО строки выбранного блока.
 */
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table';
import {
  blockTitle,
  formatDeltaPerHa,
  formatMoney,
  formatNumber,
  formatPerHa,
  techCardErrorText,
  useBlockReplacePreview,
  type BlockKind,
  type BlockReplaceResult,
  type TechCard,
} from '@/lib/planning/techCards';
import { formatMoneyNumber, formatPricePerUnit } from '@/lib/planning/money';

export function BlockReplacePanel({
  card,
  blockKind,
  sources,
  applying,
  onApply,
  onClose,
}: {
  card: TechCard;
  blockKind: BlockKind;
  sources: TechCard[];
  applying: boolean;
  onApply: (input: { sourceCardId: string; asNewCardName: string | null }) => void;
  onClose: () => void;
}) {
  const [sourceId, setSourceId] = useState<string | null>(null);
  const [newName, setNewName] = useState('');

  // Сменили блок — сбрасываем выбор: предпросмотр «защиты» на «питании» врал бы.
  useEffect(() => {
    setSourceId(null);
    setNewName('');
  }, [blockKind, card.id]);

  const preview = useBlockReplacePreview(card.id, blockKind, sourceId);

  return (
    <div className="rounded-lg border border-border bg-muted p-4">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold text-foreground">
          Замена блока «{blockTitle(blockKind)}»
        </h3>
        <Button variant="ghost" size="sm" className="ml-auto" onClick={onClose}>
          Свернуть подбор
        </Button>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">
        Набор берётся из других вариантов этой культуры. Пересчёт — сразу, до сохранения.
      </p>

      {sources.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground">
          Заменить нечем: у этой культуры нет другого варианта с блоком «
          {blockTitle(blockKind)}». Склонируйте вариант и соберите в нём свой набор — тогда
          блоки можно будет менять местами.
        </p>
      ) : (
        <>
          <div className="mt-4 grid gap-3 md:grid-cols-3">
            <div className="rounded-md border border-border bg-background p-3">
              <div className="text-sm font-medium text-foreground">
                {card.name} <span className="text-muted-foreground">— сейчас</span>
              </div>
              <div className="mt-2 text-lg font-semibold text-foreground">
                {formatPerHa(card.cost?.by_block?.[blockKind] ?? null)}
              </div>
              <div className="text-xs text-muted-foreground">блок в текущем варианте</div>
            </div>
            {sources.map((src) => (
              <button
                key={src.id}
                type="button"
                aria-pressed={src.id === sourceId}
                onClick={() => {
                  setSourceId(src.id);
                  // Имя нового варианта предлагаем сразу: рекомендуемый путь
                  // должен быть в один клик, а не «сначала придумай название».
                  setNewName(`${card.name} — ${src.name.toLowerCase()}`);
                }}
                className={
                  src.id === sourceId
                    ? 'rounded-md border border-primary bg-primary/10 p-3 text-left transition-colors'
                    : 'rounded-md border border-border bg-background p-3 text-left transition-colors hover:bg-accent'
                }
              >
                <div className="text-sm font-medium text-foreground">{src.name}</div>
                <div className="mt-2 text-lg font-semibold text-foreground">
                  {formatPerHa(src.cost?.by_block?.[blockKind] ?? null)}
                </div>
                <div className="text-xs text-muted-foreground">
                  блок из этого варианта{src.id === sourceId ? ' · выбрано' : ''}
                </div>
              </button>
            ))}
          </div>

          {sourceId && preview.isLoading && <Skeleton className="mt-4 h-40 w-full" />}

          {sourceId && preview.isError && (
            <p className="mt-4 text-sm text-destructive">{techCardErrorText(preview.error)}</p>
          )}

          {sourceId && preview.data && (
            <PreviewTable card={card} result={preview.data} />
          )}

          {sourceId && preview.data && (
            <div className="mt-4 space-y-3">
              <div className="max-w-md">
                <Label htmlFor="tc-new-variant-name">
                  Название нового варианта — рекомендуемый путь
                </Label>
                <Input
                  id="tc-new-variant-name"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder={`${card.name} — со сменой блока`}
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Без имени замена возможна только кнопкой «Заменить блок в этом варианте» —
                  тогда прежний набор блока вернуть будет нечем.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  disabled={applying || !newName.trim()}
                  onClick={() =>
                    onApply({ sourceCardId: sourceId, asNewCardName: newName.trim() })
                  }
                >
                  Сохранить как новый вариант
                </Button>
                <Button
                  variant="outline"
                  disabled={applying}
                  onClick={() => onApply({ sourceCardId: sourceId, asNewCardName: null })}
                >
                  Заменить блок в этом варианте
                </Button>
                <Button variant="ghost" disabled={applying} onClick={onClose}>
                  Отмена
                </Button>
                <span className="text-xs text-muted-foreground">
                  Замена трогает только строки блока «{blockTitle(blockKind)}» — остальные
                  операции варианта не меняются.
                </span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** «Было → станет → дельта» — та самая таблица предпросмотра из макета. */
function PreviewTable({ card, result }: { card: TechCard; result: BlockReplaceResult }) {
  const rows: { label: string; was: string; now: string; delta: string; worse: boolean }[] = [
    {
      label: 'Прямые затраты',
      was: formatPerHa(result.before.per_ha),
      now: formatPerHa(result.after.per_ha),
      delta: formatDeltaPerHa(result.delta_per_ha),
      worse: result.delta_per_ha > 0,
    },
  ];
  if (result.before.cost_per_ton !== null && result.after.cost_per_ton !== null) {
    const delta = result.after.cost_per_ton - result.before.cost_per_ton;
    rows.push({
      label: 'Себестоимость тонны',
      was: formatPricePerUnit(result.before.cost_per_ton, 'т'),
      now: formatPricePerUnit(result.after.cost_per_ton, 'т'),
      delta:
        Math.round(delta) === 0
          ? 'без изменений'
          // Дельта в той же точности, что «было» и «стало»: разная —
          // и три числа перестают сходиться на глаз (ровно жалоба AK-704).
          : `${delta < 0 ? '−' : '+'}${formatMoneyNumber(Math.abs(delta))} ₽/т`,
      worse: delta > 0,
    });
  }
  if (result.before.total !== null && result.after.total !== null) {
    const delta = result.after.total - result.before.total;
    rows.push({
      label: `Итого по ${formatNumber(result.after.area_ha)} га`,
      was: formatMoney(result.before.total),
      now: formatMoney(result.after.total),
      delta: `${delta < 0 ? '−' : '+'}${formatMoney(Math.abs(delta))}`,
      worse: delta > 0,
    });
  }
  rows.push({
    label: 'Операций в карте',
    was: String(result.before.operations.length),
    now: String(result.after.operations.length),
    delta:
      result.after.operations.length === result.before.operations.length
        ? 'столько же'
        : `${result.after.operations.length > result.before.operations.length ? '+' : '−'}${Math.abs(
            result.after.operations.length - result.before.operations.length,
          )}`,
    worse: false,
  });

  return (
    <div className="mt-4 rounded-md border border-border bg-background p-4">
      <div className="text-sm font-semibold text-foreground">
        Что станет с вариантом «{card.name}», если применить замену
      </div>
      <Table className="mt-2">
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.label}>
              <TableCell className="py-2 text-muted-foreground">{row.label}</TableCell>
              <TableCell className="py-2 text-muted-foreground line-through">{row.was}</TableCell>
              <TableCell className="py-2 font-medium text-foreground">{row.now}</TableCell>
              <TableCell
                className={
                  row.worse ? 'py-2 text-right text-destructive' : 'py-2 text-right text-primary'
                }
              >
                {row.delta}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {result.after.gaps.length > 0 && (
        <p className="mt-2 text-xs text-muted-foreground">
          Считать удалось не всё: {result.after.gaps.slice(0, 3).join('; ')}
          {result.after.gaps.length > 3 ? ' …' : ''}
        </p>
      )}
    </div>
  );
}
